/**
 * Thin fetch wrapper around `POST /v1/purchases`, `POST /v1/purchases/:id/checkout`,
 * `POST /v1/purchases/:id/checkout/verify`, and `POST /v1/purchases/:id/cancel`
 * — the Buy Now action, the subsequent gateway-order creation, the advisory
 * post-widget verification, and explicit cancellation. Mirrors
 * `infrastructure/lib/api.ts`'s `request()` auth handling (dev-bypass token,
 * force-refresh-on-401 retry) rather than `modules/entitlements/api.ts`'s
 * simpler local pattern — a money-moving surface should not silently send an
 * unauthenticated request in dev-bypass mode or give up on an expired token
 * without one retry, the way the simpler pattern would (caught in code
 * review, see `docs/features/pricing-engine-frontend/DEVELOPMENT_LOG.md`).
 *
 * These endpoints are implemented on the backend — see
 * `InviteCloudBackend/src/modules/purchase/presentation/controllers/purchase.controller.ts`.
 * This client was originally built against `05_API_SPECIFICATION.md`'s
 * approved contract ahead of the backend implementation; the shapes below
 * are verified against the real, shipped `PurchaseController`.
 */
import { API_BASE } from '@/src/infrastructure/lib/api';
import { auth } from '@/src/infrastructure/config/firebase';
import { logout } from '@/src/modules/auth/session';
import type {
  CancelPurchaseResult,
  CreateCheckoutResponse,
  CreatePurchaseRequest,
  CreateRefundRequestInput,
  Purchase,
  PurchaseListResponse,
  RefundRequest,
  VerifyCheckoutResponse,
} from './types';
import type { RazorpaySuccessResponse } from './razorpayCheckout';

/**
 * Collection is unknown/inactive, or the requested currency isn't supported
 * — `422` (API spec, Create Purchase). Always carries this same fixed,
 * customer-safe message — never the raw backend response body, which may
 * contain internal validation detail not meant for a customer to see
 * (Pricing Engine architecture §12/§16: never expose internal backend
 * errors).
 */
export class PurchaseRejectedError extends Error {
  constructor() {
    super('This product can’t be purchased right now.');
    this.name = 'PurchaseRejectedError';
  }
}

/**
 * 409 `INVITATION_ALREADY_PAID` (checkout guard, 2026-09-24): this invitation
 * is already paid for — buying again would charge twice. A PurchaseRejectedError
 * subclass so every existing `instanceof PurchaseRejectedError → show message`
 * caller shows this helpful, fixed copy (never the raw backend text).
 */
export class InvitationAlreadyPaidError extends PurchaseRejectedError {
  constructor() {
    super();
    this.message =
      'You’ve already paid for this invitation. Publish it from My Invitations → “Paid · not published” — no need to pay again.';
    this.name = 'InvitationAlreadyPaidError';
  }
}

/**
 * PQ-47: 429 rate limit error mapping for purchase creation / checkout.
 */
export class PurchaseRateLimitedError extends Error {
  constructor() {
    super('Please wait a moment and try again.');
    this.name = 'PurchaseRateLimitedError';
  }
}

async function authFetch<T>(url: string, options: RequestInit = {}, retried = false): Promise<T> {
  const isBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
  const token = isBypass ? 'dev-token' : await auth.currentUser?.getIdToken(retried).catch(() => null);
  // Retried 401s (and any other transparent retry a caller adds later) must
  // resend the exact same headers — including Idempotency-Key, if the caller
  // set one via `options.headers` — never mint or drop one mid-retry.
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string> | undefined),
    },
  });

  if (res.status === 401 && !retried && !isBypass && auth.currentUser) {
    return authFetch<T>(url, options, true);
  }
  if (res.status === 401) {
    if (isBypass) {
      throw new Error('Unauthorized: ensure backend is also running with DEV_AUTH_BYPASS=true');
    }
    await logout().catch(() => undefined);
    window.location.href = '/login';
    throw new Error('Session expired');
  }
  if (res.status === 429) {
    throw new PurchaseRateLimitedError();
  }
  if (res.status === 422) {
    throw new PurchaseRejectedError();
  }
  if (res.status === 409) {
    const body = await res.json().catch(() => null);
    if (body?.error === 'INVITATION_ALREADY_PAID') throw new InvitationAlreadyPaidError();
    throw new Error('HTTP 409');
  }
  if (!res.ok) {
    // Deliberately not surfacing the response body's message here — the
    // caller (useCreatePurchase) never shows a raw error string to the
    // customer either; this is only useful to a developer inspecting the
    // rejected promise/logs.
    throw new Error(`HTTP ${res.status}`);
  }
  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as T;
}

/**
 * The Buy Now action. Creates a Purchase in `CHECKOUT_CREATED`, with the
 * Pricing Engine consulted exactly once, server-side, and the result frozen
 * immediately — this call never sends a client-computed amount (there is no
 * `amount`/`totalPaise` field in the request shape at all).
 *
 * `idempotencyKey` is REQUIRED here whenever `request.invitationId` is
 * absent. Per `01_PURCHASE_DOMAIN.md §11`/§19 and `05_API_SPECIFICATION.md
 * §4`: once an invitation exists, the backend's own domain-level idempotency
 * key — `(userId, invitationId, collectionCode)` — is enough on its own to
 * collapse a double-click into the existing open Purchase. Before one
 * exists (this feature's only current case — see
 * `docs/features/pricing-engine-frontend/DEVELOPMENT_LOG.md`), there is no
 * durable key to collapse on, so the caller must supply a short-lived,
 * single-use `Idempotency-Key` header — the *only* protection against a
 * double-click/double-tab creating two Purchases for one intent. Every
 * caller of this function (`useCreatePurchase.createPurchaseStep`) is
 * required to always pass one.
 */
export function createPurchase(
  request: CreatePurchaseRequest,
  idempotencyKey: string,
): Promise<Purchase> {
  return authFetch<Purchase>(`${API_BASE}/purchases`, {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(request),
  });
}

/**
 * The `CHECKOUT_CREATED → PAYMENT_PENDING` transition — creates the first
 * gateway payment attempt and returns what the frontend needs to render
 * Razorpay's checkout widget. Takes no body: every input (amount, currency,
 * Collection) is already frozen on the Purchase.
 */
export function createCheckout(purchaseId: string): Promise<CreateCheckoutResponse> {
  return authFetch<CreateCheckoutResponse>(
    `${API_BASE}/purchases/${encodeURIComponent(purchaseId)}/checkout`,
    { method: 'POST' },
  );
}

/**
 * A fresh gateway order for a Purchase that is already `PAYMENT_PENDING`
 * (05_API_SPECIFICATION.md § Retry Checkout) — e.g. the user abandoned the
 * widget, left, and came back to the same per-invitation Purchase. `POST
 * /checkout` would 409 there. Same frozen amount, never re-priced.
 * `idempotencyKey` makes a double-click collapse onto one gateway order.
 */
export function retryCheckout(purchaseId: string, idempotencyKey: string): Promise<CreateCheckoutResponse> {
  return authFetch<CreateCheckoutResponse>(
    `${API_BASE}/purchases/${encodeURIComponent(purchaseId)}/checkout/retry`,
    { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey } },
  );
}

export interface ConfirmedPurchase {
  purchaseId: string;
  orderNumber: string;
  status: string;
  /** The invitation this was bought for; null for a catalog purchase. */
  invitationId: string | null;
}

/**
 * Paid-but-page-lost recovery (2026-09-24, ORD-2026-000007): asks the backend
 * to confirm the caller's recent pending payments with Razorpay and complete
 * the paid ones right away. The dashboard calls it once on load.
 */
export function confirmPendingPayments(): Promise<{ confirmed: ConfirmedPurchase[] }> {
  return authFetch<{ confirmed: ConfirmedPurchase[] }>(`${API_BASE}/purchases/confirm-pending`, { method: 'POST' });
}

/**
 * Relays the exact triple Razorpay's Checkout.js `handler` returned so the
 * backend can HMAC-verify it against the order it issued
 * (`docs/architecture/payment/razorpay/checkout-flow.md §6`). Since
 * 2026-09-24 the backend also asks Razorpay whether the payment is captured
 * and, if so, completes the Purchase inside this request (the "fast path"),
 * so the first `getPurchase` poll usually already sees COMPLETED. The
 * response is still not the source of truth: callers keep polling
 * `getPurchase`, and treat a rejection (400 tampered triple, 404
 * not-our-order, network) as non-fatal for the UI. The signature is relayed
 * as-is; the frontend never holds any secret that could compute or check it.
 */
export function verifyCheckout(
  purchaseId: string,
  result: RazorpaySuccessResponse,
): Promise<VerifyCheckoutResponse> {
  return authFetch<VerifyCheckoutResponse>(
    `${API_BASE}/purchases/${encodeURIComponent(purchaseId)}/checkout/verify`,
    {
      method: 'POST',
      body: JSON.stringify({
        razorpayOrderId: result.razorpay_order_id,
        razorpayPaymentId: result.razorpay_payment_id,
        razorpaySignature: result.razorpay_signature,
      }),
    },
  );
}

/**
 * Explicit customer cancellation, from either `CHECKOUT_CREATED` or
 * `PAYMENT_PENDING` (`05_API_SPECIFICATION.md`'s Cancel Purchase entry).
 * `reason` is accepted for analytics only, never a business input. Response
 * is deliberately minimal per that same entry — `{id, status, version}`,
 * not the full `Purchase` shape.
 */
export function cancelPurchase(purchaseId: string, reason?: string): Promise<CancelPurchaseResult> {
  return authFetch<CancelPurchaseResult>(`${API_BASE}/purchases/${encodeURIComponent(purchaseId)}/cancel`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });
}

/**
 * Poll target for "did the backend confirm this payment yet." Never trust a
 * client-side Razorpay success callback as authoritative (state-machine doc
 * §"Who/what can trigger each transition" — PAYMENT_COMPLETED is only ever
 * driven by a backend-verified webhook) — after Razorpay's `handler` fires,
 * the caller polls this until `status` moves off `PAYMENT_PENDING`.
 */
export function getPurchase(purchaseId: string): Promise<Purchase> {
  return authFetch<Purchase>(`${API_BASE}/purchases/${encodeURIComponent(purchaseId)}`, {
    method: 'GET',
  });
}

/** Order history — the "view eligible purchase" step of LEVEL_12_REFUND_AND_CANCELLATION.md §18.1. */
export function listPurchases(cursor?: string): Promise<PurchaseListResponse> {
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  return authFetch<PurchaseListResponse>(`${API_BASE}/purchases${qs}`, { method: 'GET' });
}

export class InvoiceDownloadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvoiceDownloadError';
  }
}

const INVOICE_ERROR_MESSAGES: Record<number, string> = {
  401: 'Your session expired — please refresh the page and sign in again.',
  404: 'We couldn’t find an invoice for this order.',
  409: 'This invoice isn’t available yet — it appears once payment is confirmed.',
  429: 'Please wait a moment and try again.',
  503: 'We couldn’t generate your invoice right now. Please try again, or contact support if it keeps happening.',
};

/**
 * Downloads the PDF invoice for one of the caller's purchases. Auth is a Bearer
 * header, so this is a fetch → Blob → temporary anchor (a plain `<a href>` can't
 * send it, and a signed URL would put a credential in the query string).
 */
export async function downloadInvoice(purchaseId: string, invoiceNumber?: string): Promise<void> {
  const isBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
  const token = isBypass ? 'dev-token' : await auth.currentUser?.getIdToken().catch(() => null);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/purchases/${encodeURIComponent(purchaseId)}/invoice/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch {
    throw new InvoiceDownloadError('Network error — check your connection and try again.');
  }
  if (!res.ok) {
    throw new InvoiceDownloadError(INVOICE_ERROR_MESSAGES[res.status] ?? 'Couldn’t download the invoice. Please try again.');
  }
  const match = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '');
  const url = URL.createObjectURL(await res.blob());
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = match?.[1] ?? (invoiceNumber ? `${invoiceNumber}.pdf` : 'invoice.pdf');
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Deferred: revoking synchronously can cancel the download in some Safari versions.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

/**
 * Maps `BillingExceptionFilter`'s error `code` (`REFUND_REQUEST_ALREADY_PENDING`,
 * `NOTHING_LEFT_TO_REFUND`, `REFUND_EXCEEDS_REMAINING`, ...) to copy that's
 * actually safe to show a customer — the raw backend `message` for these
 * interpolates internal UUIDs (payment/request ids) and backend-vocabulary
 * phrases ("COMPLETED — no captured payment to refund") directly into the
 * string, which is not customer-safe despite the error CLASSES being
 * intentionally customer-facing (caught in review — a prior version of this
 * function rendered the raw backend message verbatim).
 */
const REFUND_ERROR_MESSAGES: Record<string, string> = {
  REFUND_REQUEST_ALREADY_PENDING: 'You already have a refund request pending review for this purchase.',
  NOTHING_LEFT_TO_REFUND: 'This purchase has already been fully refunded.',
  REFUND_EXCEEDS_REMAINING: 'That amount is more than what remains refundable for this purchase.',
  PURCHASE_NOT_REFUND_ELIGIBLE: 'This purchase isn’t eligible for a refund yet.',
  REFUND_REQUEST_IDEMPOTENCY_CONFLICT: 'Something went wrong submitting your request — please try again.',
  INVALID_REFUND_AMOUNT: 'Please enter a valid refund amount.',
  REFUND_NO_FAULT_FLAGGED:
    'We have not detected a problem with your invitation. If something looks wrong, please contact support@invitecloud.in and we will check it.',
  REFUND_WINDOW_EXPIRED:
    'The 24-hour window to request a refund for this problem has closed. Please contact support@invitecloud.in.',
  // FI-03 option A: an extra payment we kept as wallet credit
  WALLET_CREDIT_REFUND_WINDOW_CLOSED:
    'This extra payment was added to your wallet more than 7 days ago, so it stays as wallet credit. Contact support@invitecloud.in if you need help.',
  WALLET_CREDIT_ALREADY_USED:
    'Some of the credit from this extra payment has already been used, so it can’t be refunded to your bank.',
};

export class RefundRequestApiError extends Error {
  readonly code: string | undefined;
  constructor(message: string, code: string | undefined) {
    super(message);
    this.name = 'RefundRequestApiError';
    this.code = code;
  }
}

async function refundAuthFetch<T>(url: string, options: RequestInit = {}, retried = false): Promise<T> {
  const isBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
  const token = isBypass ? 'dev-token' : await auth.currentUser?.getIdToken(retried).catch(() => null);
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string> | undefined),
    },
  });

  if (res.status === 401 && !retried && !isBypass && auth.currentUser) {
    return refundAuthFetch<T>(url, options, true);
  }
  if (res.status === 401) {
    await logout().catch(() => undefined);
    window.location.href = '/login';
    throw new Error('Session expired');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const code: string | undefined = typeof body?.error === 'string' ? body.error : undefined;
    const friendly = code ? REFUND_ERROR_MESSAGES[code] : undefined;
    throw new RefundRequestApiError(friendly ?? 'Something went wrong. Please try again.', code);
  }
  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as T;
}

/**
 * Creates a refund REQUEST, never a refund. The user never calls Razorpay or
 * executes anything directly — an admin must still review and approve
 * (LEVEL_12_REFUND_AND_CANCELLATION.md §6/§18.1). `idempotencyKey` required,
 * same double-click protection convention as `createPurchase` above.
 */
export function createRefundRequest(
  purchaseId: string,
  input: CreateRefundRequestInput,
  idempotencyKey: string,
): Promise<RefundRequest> {
  return refundAuthFetch<RefundRequest>(`${API_BASE}/purchases/${encodeURIComponent(purchaseId)}/refund-requests`, {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(input),
  });
}

export function listRefundRequests(purchaseId: string): Promise<{ items: RefundRequest[] }> {
  return refundAuthFetch<{ items: RefundRequest[] }>(
    `${API_BASE}/purchases/${encodeURIComponent(purchaseId)}/refund-requests`,
    { method: 'GET' },
  );
}

/**
 * PQ-33: Customer withdraws their own pending refund request.
 */
export function cancelRefundRequest(purchaseId: string, requestId: string): Promise<RefundRequest> {
  return refundAuthFetch<RefundRequest>(
    `${API_BASE}/purchases/${encodeURIComponent(purchaseId)}/refund-requests/${encodeURIComponent(requestId)}/cancel`,
    { method: 'POST' },
  );
}

/** Go-live B6: same BILLING_ENABLED gate the backend uses for refund create/approve. Authenticated via Bearer token, fails closed passively without disruptive redirects. */
export async function getBillingCapabilities(): Promise<{ refundExecutionAvailable: boolean }> {
  try {
    const isBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
    const token = isBypass ? 'dev-token' : await auth.currentUser?.getIdToken().catch(() => null);
    const res = await fetch(`${API_BASE}/billing/capabilities`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!res.ok) {
      // Fail closed: 401, 403, 500, etc. treats refunds as unavailable without logging out the user.
      return { refundExecutionAvailable: false };
    }
    const body = (await res.json()) as {
      data?: { refundExecutionAvailable: boolean };
      refundExecutionAvailable?: boolean;
    };
    const payload = body?.data ?? body;
    return { refundExecutionAvailable: payload.refundExecutionAvailable === true };
  } catch {
    // Fail closed: Network or parse failure treats refunds as unavailable without blanking the purchases list.
    return { refundExecutionAvailable: false };
  }
}
